/**
 * Passwordless sign-in.
 *
 * WHY the "check your email" screen is a separate state and not a toast: POST /auth/request
 * answers 202 whether or not the account exists (the API refuses to leak which emails are
 * registered), so the UI has nothing to confirm — it can only tell the seller what to do next.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { api, API_URL, ApiError } from "../api";
import { useSession } from "../session";
import { Loading } from "../components/States";

type Phase = { step: "form" } | { step: "sending" } | { step: "sent"; email: string } | { step: "error"; message: string };

/** The dev host writes the magic link to the API log instead of sending mail (MAIL_TRANSPORT=log). */
const DEV_HINT_COMMAND = "journalctl -u payoutaudit-api -f";

export function Login(): ReactNode {
  const { session } = useSession();
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState<Phase>({ step: "form" });

  if (session.status === "loading") return <div className="page centered"><Loading label="Checking your session" /></div>;
  if (session.status === "authenticated") return <Navigate to="/" replace />;

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setPhase({ step: "sending" });
    api.requestLogin(value).then(
      () => setPhase({ step: "sent", email: value }),
      (err: unknown) => setPhase({ step: "error", message: err instanceof ApiError ? err.message : "Could not request a sign-in link." }),
    );
  };

  return (
    <div className="page centered">
      <section className="card auth-card">
        <h1 className="auth-title">PayoutAudit</h1>
        <p className="auth-sub">Payout reconciliation for Shopify, TikTok Shop and Amazon sellers.</p>

        {phase.step === "sent" ? (
          <>
            <p className="state-title">Check your email</p>
            <p className="state-hint">
              If an account exists for <strong>{phase.email}</strong>, a sign-in link is on its way. The link works once
              and expires in 15 minutes.
            </p>
            <button type="button" className="btn" onClick={() => setPhase({ step: "form" })}>Use a different email</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <label className="field">
              <span className="field-label">Email</span>
              <input
                className="input"
                type="email"
                name="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={phase.step === "sending"}
              />
            </label>
            <button type="submit" className="btn btn-primary btn-block" disabled={phase.step === "sending" || email.trim() === ""}>
              {phase.step === "sending" ? "Sending…" : "Email me a sign-in link"}
            </button>
            {phase.step === "error" ? <p className="form-error" role="alert">{phase.message}</p> : null}
          </form>
        )}

        <p className="hint auth-dev">
          API: <code>{API_URL}</code>. On the dev host no mail is sent — the link is printed in the API log:
          <code>{DEV_HINT_COMMAND}</code>
        </p>
      </section>
    </div>
  );
}
